import React, { useEffect, useState } from 'react';

import Highcharts from 'highcharts';
import HighchartsWrapper from '../../../../components/HighchartsWrapper';
import HighchartsExporting from 'highcharts/modules/exporting';

import { baseGraphConfigs } from './configs';
import { MultiMetricData, MultiMetricValue, GraphLayoutProps } from '../../../../types';
import { handleValueORPercentage } from '../../../../helpers/format';
import GraphLegend from '../../../../components/GraphLegend';
import { getDatesArrayFromData } from '../../helpers';

// extends export functionality to the main highcharts library
if (typeof Highcharts === 'object') {
  HighchartsExporting(Highcharts);
}

interface Props {
  graphData: MultiMetricData[];
  dateRange?: string[] | null;
  lineGraphEl: any;
  selectedCurrency: string | null;
  valueOrPercentage?: string | null;
  graphLayout?: GraphLayoutProps | null;
  updateGraphVisibility: (visible: boolean, id: string) => void;
}

const Graph = ({
  graphData,
  dateRange,
  lineGraphEl,
  selectedCurrency,
  valueOrPercentage,
  graphLayout,
  updateGraphVisibility
}: Props): React.ReactElement => {
  const [dates, setDates] = useState<string[] | null>(null);
  const [legends, setLegends] = useState<any | null>(null);
  const decimalPlaces = 2;

  const renderTooltip = (date: string) => {
    const graph: Highcharts.Chart = lineGraphEl?.current?.chart;

    const dateValues = sortedGraphData(graphData).map((g) => {
      const series: any = graph.get(g.dimension);
      const pointObject = {
        currentValueType: g.currentValueType,
        color: g.color,
        label: g.label,
        dateValue: g.valuesWithDates.filter((vwd) => vwd.date === date)
      };
      return series.visible ? pointObject : null;
    });

    return dateValues
      .filter((a) => a)
      .reduce(function (s: any, point: any) {
        const isPercentage = valueOrPercentage && valueOrPercentage === 'percentage';
        const value = isPercentage ? point.dateValue[0]?.percentage : point.dateValue[0]?.value;
        const parsedValue = handleValueORPercentage(
          value,
          isPercentage ? 'percentage' : point.currentValueType,
          selectedCurrency
        );
        return (
          s +
          '<br/>' +
          `<span style="color:${point.color}">\u25CF </span> &nbsp;` +
          point.label +
          ': ' +
          `<b>${parsedValue}</b>`
        );
      }, '<b>' + date + '</b>');
  };

  useEffect(() => {
    if (graphData && graphData.length > 0) {
      const dates = getDatesArrayFromData(graphData);
      if (dateRange) {
        setDates(dateRange);
      } else {
        setDates(dates);
      }

      const legendData = [...graphData].map((m: MultiMetricData) => {
        const legendItem = {
          id: m.dimension,
          type: 'string',
          color: m.color,
          name: m.label,
          data: []
        };
        return legendItem;
      });
      setLegends(legendData);
    } else {
      setLegends(null);
    }
  }, [graphData]);

  const getYAxis = (valueType: string): number => {
    let axisNumber = 0;
    switch (valueType) {
      case 'currency':
        axisNumber = 0;
        break;
      case 'percentage':
        axisNumber = 1;
        break;
      case 'scalar':
        axisNumber = 2;
        break;

      default:
        break;
    }
    return axisNumber;
  };

  const sortedGraphData = (data: Array<MultiMetricData>) => {
    //copy graph data and sort by highest total value to get stacked graphs with highest values on top
    const dataCopy = [...data];
    const sorted = dataCopy.sort((a, b) => {
      const aValues = a.valuesWithDates.reduce((c, d) => c + d.value, 0);
      const bValues = b.valuesWithDates.reduce((c, d) => c + d.value, 0);

      return aValues < bValues ? 1 : -1;
    });
    return sorted;
  };

  const plotValuesToDates = (valuesData: MultiMetricValue[]) => {
    //safety for when some dimensions return different amount of valuesWithDates
    const plotted = dates?.map((date) => {
      const dateValue = valuesData.filter((v) => v.date === date);
      if (dateValue.length) {
        return valueOrPercentage && valueOrPercentage === 'percentage'
          ? dateValue[0].percentage || null
          : dateValue[0].value;
      } else {
        return null;
      }
    });

    return plotted;
  };

  const lineGraphOptions: Highcharts.Options = {
    ...baseGraphConfigs,
    chart: {
      height: '540'
    },
    plotOptions: {
      ...baseGraphConfigs.plotOptions,
      series: {
        states: {
          inactive: {
            opacity: 1
          }
        }
      },
      line: {
        dataLabels: {
          enabled: true,
          formatter: function () {
            const valueType =
              valueOrPercentage && valueOrPercentage === 'percentage'
                ? 'percentage'
                : this.series.options.custom?.currentValueType;
            return handleValueORPercentage(this.y, valueType, selectedCurrency);
          }
        },
        boostThreshold: 1
      },
      column: {
        stacking: graphLayout && graphLayout.id === 'bar-stacked' ? 'normal' : undefined,
        dataLabels: {
          enabled: true,
          formatter: function () {
            const valueType =
              valueOrPercentage && valueOrPercentage === 'percentage'
                ? 'percentage'
                : this.series.options.custom?.currentValueType;
            return handleValueORPercentage(this.y, valueType, selectedCurrency);
          }
        },
        boostThreshold: 1
      },
      area: {
        dataLabels: {
          enabled: true,
          formatter: function () {
            const valueType =
              valueOrPercentage && valueOrPercentage === 'percentage'
                ? 'percentage'
                : this.series.options.custom?.currentValueType;
            return handleValueORPercentage(this.y, valueType, selectedCurrency);
          }
        }
      }
    },
    tooltip: {
      formatter: function () {
        return renderTooltip(this.x.toString());
      },
      shared: true,
      outside: true,
      style: {
        zIndex: 100
      }
    },
    xAxis: {
      categories: dates || []
    },
    yAxis: [
      {
        labels: {
          formatter: function () {
            const labelValue = handleValueORPercentage(this.value, 'currency', selectedCurrency, decimalPlaces);
            return labelValue;
          }
        },
        title: undefined,
        opposite: false
      },
      {
        labels: {
          formatter: function () {
            const labelValue = handleValueORPercentage(this.value, 'percentage', selectedCurrency);
            return labelValue;
          }
        },
        title: undefined,
        opposite: false
      },
      {
        labels: {
          formatter: function () {
            const labelValue = handleValueORPercentage(this.value, 'scalar', selectedCurrency, decimalPlaces);
            return labelValue;
          }
        },
        title: undefined,
        opposite: true
      }
    ],
    series: sortedGraphData(graphData)?.map((g: MultiMetricData) => {
      return {
        id: g.dimension,
        name: g.label,
        type: graphLayout ? (graphLayout.type as 'line' | 'column' | 'area') : 'line',
        color: g.color,
        data: plotValuesToDates(g.valuesWithDates) || [],
        yAxis: getYAxis(valueOrPercentage && valueOrPercentage === 'percentage' ? 'percentage' : g.currentValueType),
        custom: {
          currentValueType: valueOrPercentage && valueOrPercentage === 'percentage' ? 'percentage' : g.currentValueType
        }
      };
    }),
    exporting: {
      enabled: false,
      sourceWidth: 1613,
      sourceHeight: 483
    }
  };

  const toggleVisibility = (visible: boolean, id: string) => {
    const graph: Highcharts.Chart = lineGraphEl?.current?.chart;
    const series: any = graph.get(id);
    if (series.visible !== visible) {
      visible ? series?.show() : series?.hide();
      updateGraphVisibility(visible, id);
    }
  };

  return (
    <>
      <HighchartsWrapper
        lineGraphEl={lineGraphEl}
        graphOptions={graphData && graphData.length > 0 ? lineGraphOptions : null}
      />
      <div className="my-5 mx-0 pb-16">
        {legends &&
          legends.map((legend: Highcharts.SeriesOptionsType, index: number) => (
            <GraphLegend
              key={`${legend.id}-${index}`}
              legend={legend}
              label={legend.name}
              variant="scratchpad"
              toggleVisibility={(value) => toggleVisibility(value, legend.id || '')}
            />
          ))}
      </div>
    </>
  );
};

export default Graph;
